import Paper from "@material-ui/core/Paper";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Tooltip from "@material-ui/core/Tooltip";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  StyledTableCell,
  StyledTableRow,
  useStyles,
} from "../profiles/ProfileStyles";
import Actions from "./ExternalUsersActions";
import { compareStrings } from "utils/sort/strings";
import { createContent } from "utils/tables/createContent";

const useStyles1 = makeStyles(theme => ({
  root: {
    flexShrink: 0,
    flexGrow: 1,
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(2.5),
  },
  paginationTop: {
    float: "right",
  },
}));

function desc(a, b, orderBy) {
  let valueA = a[orderBy];
  let valueB = b[orderBy];

  if (orderBy === "userExternalPlaces") {
    valueA =
      a[orderBy] && a[orderBy][0] && a[orderBy][0].place
        ? a[orderBy][0].place.name
        : "";
    valueB =
      b[orderBy] && b[orderBy][0] && b[orderBy][0].place
        ? b[orderBy][0].place.name
        : "";
  }

  if (a[orderBy] === null || a[orderBy] === undefined) {
    a[orderBy] = "";
  }
  if (b[orderBy] === null || b[orderBy] === undefined) {
    b[orderBy] = "";
  }

  return compareStrings(valueA.toString(), valueB.toString());
}

const ExternalUsersTable = props => {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rows, setRows] = useState({});
  const [rowsLength, setRowsLength] = useState(0);
  const [headers, setHeaders] = useState([]);
  const [mainField, setMainField] = useState("");
  const [visibleHeaders, setVisibleHeaders] = useState([]);
  const [sortedFields, setSortedFields] = useState([]);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");

  React.useEffect(() => {
    setHeaders(props.headers);
    setRows(props.rows);
    setRowsLength(props.rows.length);
    setMainField(props.mainField);
    setVisibleHeaders(props.visibleHeaders);
    setSortedFields(props.sortedFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.headers, props.mainField, props.rows, props.visibleHeaders]);

  function handlePageChange(event, newPage) {
    setPage(newPage);
  }

  const handleRowsPerPageChange = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (event, property) => {
    const isDesc = orderBy === sortedFields[property] && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(sortedFields[property]);
  };

  const UsersTableHead = props => {
    const { order, orderBy, onRequestSort } = props;
    const [t] = useTranslation();
    const paginationTop = action => e => {
      var selector = "[aria-label^='" + action + "']";
      document.querySelector(selector).click();
      e.preventDefault();
    };
    const createSortHandler = property => event => {
      onRequestSort(event, property);
    };

    return (
      <TableHead>
        <TableRow>
          {visibleHeaders.map((header, index) => (
            <StyledTableCell
              key={index}
              size="small"
              align="left"
              sortDirection={orderBy === sortedFields[index] ? order : false}
            >
              <TableSortLabel
                active={orderBy === sortedFields[index]}
                direction={order}
                onClick={createSortHandler(index)}
                disabled={index === 0 || index === 5}
              >
                {header.toUpperCase()}
                {orderBy === sortedFields[index] ? (
                  <span className={classes.visuallyHidden}>
                    {order === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </span>
                ) : null}
              </TableSortLabel>
            </StyledTableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  };

  UsersTableHead.propTypes = {
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.oneOf(["asc", "desc"]).isRequired,
    orderBy: PropTypes.string.isRequired,
  };

  function getPlaceList(row) {
    const response = [];
    if (
      row["userExternalPlaces"] !== undefined ||
      row["userExternalPlaces"] !== null ||
      row["userExternalPlaces"].length !== 0
    ) {
      for (let i = 0; i < row["userExternalPlaces"].length; i++) {
        const item = row["userExternalPlaces"][i];
        if (item && item.place) {
          response.push(item.place.name);
        }
      }
    }

    if (response.length === 0) response.push("-");

    return response;
  }

  return (
    <Paper className={classes.root}>
      <div className={classes.tableWrapper}>
        <Table className={classes.table} size="small">
          {rows.length > 0 && (
            <UsersTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
          )}
          <TableBody>
            {createContent(rows, headers, order, orderBy, desc)
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map(row => (
                <StyledTableRow key={row.id}>
                  <TableCell size="small" component="th" scope="row">
                    <div className={classes.avatarParent}>
                      {row.imagePath ? (
                        <div
                          className="avatar"
                          style={{
                            backgroundImage: `url(${row.imagePath})`,
                          }}
                        ></div>
                      ) : (
                        <div
                          className="avatar"
                          style={{
                            backgroundImage: `url(/images/users/icon-default.png)`,
                          }}
                        ></div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell size="small">{row["name"]}</TableCell>
                  <TableCell size="small">{row["phoneNumber"]}</TableCell>
                  <TableCell size="small">{row["email"]}</TableCell>
                  <TableCell size="small">
                    {getPlaceList(row)[0]}
                    {getPlaceList(row).length > 1 && (
                      <Tooltip
                        title={getPlaceList(row).splice(1).join(" e ")}
                        aria-label="details"
                        placement="top"
                      >
                        <span
                          style={{ fontWeight: "bold", color: "#6D6F71" }}
                        >{` +1`}</span>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell size="small" align="right">
                    <div
                      style={{
                        marginTop: "auto",
                        marginBottom: "auto",
                        display: "flex",
                        flexWrap: "nowrap ",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Actions item={row} permission={props.permission} />
                    </div>
                  </TableCell>
                </StyledTableRow>
              ))}
          </TableBody>
        </Table>
        {rows.length > 0 && (
          <TableFooter style={{ float: "right" }}>
            <TablePagination
              labelRowsPerPage={"Resultados por página"}
              count={rowsLength}
              rowsPerPage={rowsPerPage}
              page={page}
              component="div"
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </TableFooter>
        )}
      </div>
    </Paper>
  );
};

ExternalUsersTable.propTypes = {
  rows: PropTypes.array.isRequired,
  headers: PropTypes.array.isRequired,
  visibleHeaders: PropTypes.array.isRequired,
  mainField: PropTypes.string.isRequired,
};

export default ExternalUsersTable;
